import { createSlice } from "@reduxjs/toolkit"
import { persistReducer } from "redux-persist"
import storage from "redux-persist/lib/storage"

import { frozenStateReconciler } from "../../../../Utils/persistHelpers"
import { logout } from "../../../LoginPage/redux/auth.actions"
import { preferencesSuccess } from "../../../User/redux/user.actions"
import { PUBLIC_API_SLICE_NAME } from "./publicApi.constants"
import {
  addEndpoint,
  addTypes,
  changeEndpointStatus,
  deleteEndpoint,
  getApiKeys,
  getEndpoint,
  getEndpoints,
  getEvent,
  getEvents,
  resendEvent
} from "./publicApi.thunks"

/* ------------- Initial State ------------- */

const emptyApiKeys = () => ({
  count: 0,
  next: null,
  previous: null,
  results: []
})

const initialState = {
  apiKeys: emptyApiKeys(),
  apiKeysFetching: true,
  apiKeysError: null,

  //webhooks data
  endpoints: {
    count: 0,
    results: []
  },
  events: {
    count: 0,
    results: []
  },
  requestResult: null,

  webhooksFetching: false,

  endpointData: null,

  filtersEvents: {
    event_name: [],
    source_id: [],
    created_timestamp: null,
    exclude_fields: []
  },
  // The legacy reducer only ever created this through `state.merge(...)` in
  // `publicApiSetProp`, so it was missing from its INITIAL_STATE - Immer needs it declared.
  filtersLastUpd: null,
  filterWebhookAttempts: "all",
  eventData: null
}

// Bump when the shape of the saved `{ version, filtersEvents }` payload changes.
export const PUBLIC_API_EVENTS_PREFERENCES_VERSION = 1

const slice = createSlice({
  name: PUBLIC_API_SLICE_NAME,
  initialState,
  reducers: {
    // Legacy call sites dispatched this with two arguments - `publicApiSetProp(key, value)`.
    // RTK actions carry a single payload, so callers pass `publicApiSetProp({ key, value })`,
    // matching the `alertsSetProp`/`userSetProp` slices.
    publicApiSetProp(state, action) {
      const { key, value } = action.payload

      if (["filtersEvents"]?.includes(key)) {
        if (key === "filtersEvents") state.filtersLastUpd = new Date().getTime()
        state[key] = { ...state?.[key], ...value }
      } else {
        state[key] = value
      }
    },
    requestResultReset(state) {
      state.requestResult = null
    }
  },
  extraReducers: (builder) => {
    builder
      /* ---------------- api keys ---------------- */
      .addCase(getApiKeys.pending, (state) => {
        state.apiKeysFetching = true
      })
      .addCase(getApiKeys.fulfilled, (state, { payload }) => {
        state.apiKeys = payload || emptyApiKeys()
        state.apiKeysFetching = false
      })
      // The legacy failure reducer only set the error message and left `apiKeysFetching`
      // alone - nothing reads that flag, so it is carried over unchanged.
      .addCase(getApiKeys.rejected, (state) => {
        state.apiKeysError = "Failed to load api keys"
      })

      /* ---------------- webhook endpoints ---------------- */
      .addCase(getEndpoints.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(getEndpoints.fulfilled, (state, { payload }) => {
        state.webhooksFetching = false
        state.endpoints = payload
      })
      .addCase(getEndpoints.rejected, (state) => {
        state.webhooksFetching = false
      })

      .addCase(getEndpoint.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(getEndpoint.fulfilled, (state, { payload }) => {
        state.webhooksFetching = false
        state.endpointData = payload
      })
      .addCase(getEndpoint.rejected, (state) => {
        state.webhooksFetching = false
      })

      // The four write requests below only ever drove the shared spinner - the pages they
      // live on re-fetch their own data from the `.unwrap()` success callback.
      .addCase(changeEndpointStatus.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(changeEndpointStatus.fulfilled, (state) => {
        state.webhooksFetching = false
      })
      .addCase(changeEndpointStatus.rejected, (state) => {
        state.webhooksFetching = false
      })

      .addCase(deleteEndpoint.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(deleteEndpoint.fulfilled, (state) => {
        state.webhooksFetching = false
      })
      .addCase(deleteEndpoint.rejected, (state) => {
        state.webhooksFetching = false
      })

      .addCase(addEndpoint.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(addEndpoint.fulfilled, (state) => {
        state.webhooksFetching = false
      })
      .addCase(addEndpoint.rejected, (state) => {
        state.webhooksFetching = false
      })

      .addCase(addTypes.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(addTypes.fulfilled, (state) => {
        state.webhooksFetching = false
      })
      .addCase(addTypes.rejected, (state) => {
        state.webhooksFetching = false
      })

      /* ---------------- webhook events ---------------- */
      .addCase(getEvents.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(getEvents.fulfilled, (state, { payload }) => {
        state.webhooksFetching = false
        state.events = payload
      })
      .addCase(getEvents.rejected, (state) => {
        state.webhooksFetching = false
      })

      .addCase(getEvent.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(getEvent.fulfilled, (state, { payload }) => {
        state.webhooksFetching = false
        state.eventData = payload
      })
      .addCase(getEvent.rejected, (state) => {
        state.webhooksFetching = false
      })

      .addCase(resendEvent.pending, (state) => {
        state.webhooksFetching = true
      })
      .addCase(resendEvent.fulfilled, (state) => {
        state.webhooksFetching = false
      })
      .addCase(resendEvent.rejected, (state) => {
        state.webhooksFetching = false
      })

      /* ---------------- saved per-user table preferences ---------------- */
      .addCase(preferencesSuccess, (state, { payload }) => {
        const preference = payload?.["public-api-events"]?.data
        if (!preference || preference.version !== PUBLIC_API_EVENTS_PREFERENCES_VERSION) return
        if (!preference.filtersEvents) return

        state.filtersEvents = preference.filtersEvents
      })

      /* ---------------- logout ---------------- */
      // The legacy reducer had no LOGOUT case, so one account's API keys, endpoints and
      // events stayed in the persisted store for the next user until each page re-fetched.
      .addCase(logout, () => initialState)
  }
})

export const { publicApiSetProp, requestResultReset } = slice.actions

// `generateApiKey`/`regenerateApiKey` get no cases on purpose: the legacy reducer returned
// state untouched for all three of their phases, and the refreshed list arrives through the
// `getApiKeys` call each thunk chains on success.

const PERSISTED_REDUCER_VERSION = 2

export const persistedReducer = persistReducer(
  {
    key: PUBLIC_API_SLICE_NAME,
    storage,
    // Only the user's own filter setup survives a reload - API keys, endpoints and events
    // are all re-fetched on mount.
    whitelist: ["filtersEvents", "filtersLastUpd", "filterWebhookAttempts"],
    stateReconciler: frozenStateReconciler,
    version: PERSISTED_REDUCER_VERSION,
    migrate: async (state) => {
      if (state?._persist?.version < PERSISTED_REDUCER_VERSION) {
        return {
          ...state,
          ...initialState
        }
      }
      return state
    }
  },
  slice.reducer
)

export const reducer = persistedReducer
